import { cityText as t } from '@muraletta/city-i18n';

// Public geographical names only. No products, images, AI calls or customer data.
const configNode = document.querySelector('[data-muraletta-city-config]');
const config = configNode ? JSON.parse(configNode.textContent) : null;
const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
const countryCache = new Map();
const chunkCache = new Map();
let manifestPromise;
export const normalizeCity = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase().replace(/[أإآ]/g, 'ا').replace(/ى/g, 'ي').replace(/[’'`ʻʼ]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const countryName = code => {
  try { return new Intl.DisplayNames([document.documentElement.lang || 'fr'], { type: 'region' }).of(code); }
  catch { return code; }
};
const node = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
};
function cityUrl(country, id) {
  const url = new URL(`${root}pages/ville`, location.origin);
  url.searchParams.set('pays', country);
  url.searchParams.set('id', String(id));
  if (new URL(location.href).searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', new URL(location.href).searchParams.get('preview_theme_id'));
  return url;
}
function directoryUrl(country, query = '') {
  const url = new URL(`${root}pages/villes`, location.origin);
  if (country) url.searchParams.set('pays', country);
  if (query) url.searchParams.set('q', query);
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}
async function manifest() {
  if (!config) throw new Error('City configuration missing');
  if (!manifestPromise) manifestPromise = fetch(config.manifest, { credentials: 'omit' }).then(async response => {
    if (!response.ok) throw new Error('City manifest unavailable');
    const value = await response.json();
    if (!value.countries || !Array.isArray(value.fields)) throw new Error('Invalid city manifest');
    return value;
  }).catch(error => { manifestPromise = null; throw error; });
  return manifestPromise;
}
async function chunk(part) {
  if (!/^muraletta-cities-[A-Z]{2}-\d+\.json$/.test(part.file)) throw new Error('Invalid city chunk');
  if (!chunkCache.has(part.file)) {
    const url = new URL(part.file, new URL(config.manifest, location.href));
    url.searchParams.set('v', part.sha256.slice(0, 16));
    chunkCache.set(part.file, fetch(url, { credentials: 'omit' }).then(async response => {
      if (!response.ok) throw new Error('City chunk unavailable');
      const rows = await response.json();
      if (!Array.isArray(rows) || rows.length !== part.count) throw new Error('Invalid city chunk');
      return rows;
    }).catch(error => { chunkCache.delete(part.file); throw error; }));
  }
  return chunkCache.get(part.file);
}
function currentCountry() {
  return document.documentElement.dataset.inspirationCountry || document.documentElement.dataset.countryDefault || 'FR';
}
async function selectedCountry(explicit) {
  if (!explicit && !document.documentElement.dataset.inspirationCountry) {
    await new Promise(resolve => {
      const finish = () => { clearTimeout(timer); document.removeEventListener('muraletta:country-detected', finish); resolve(); };
      const timer = setTimeout(finish, 3500);
      document.addEventListener('muraletta:country-detected', finish, { once: true });
    });
  }
  const data = await manifest();
  const selected = /^[A-Z]{2}$/.test(explicit || '') ? explicit : currentCountry();
  return { data, code: selected, country: data.countries[selected] };
}
async function countryRows(code, onProgress = () => {}) {
  if (countryCache.has(code)) return countryCache.get(code);
  const operation = (async () => {
    const data = await manifest();
    const country = data.countries[code];
    if (!country) return [];
    const queue = [...country.chunks], rows = [];
    await Promise.all(Array.from({ length: Math.min(2, queue.length) }, async () => {
      while (queue.length) {
        rows.push(...await chunk(queue.shift()));
        onProgress(rows);
        // Yield between chunks so typing and navigation remain responsive.
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }));
    return rows;
  })().catch(error => { countryCache.delete(code); throw error; });
  countryCache.set(code, operation);
  return operation;
}
const searchIndexes = new WeakMap();
function rowSearch(row) {
  if (!searchIndexes.has(row)) searchIndexes.set(row, normalizeCity(`${row[1]} ${row[2] || ''} ${row[4] || ''}`));
  return searchIndexes.get(row);
}
export function matchesCity(row, query) {
  const tokens = normalizeCity(query).split(' ').filter(Boolean);
  const text = rowSearch(row);
  return tokens.every(token => text.includes(token));
}
function region(row, country) { return country.regions?.[row[3]] || row[3] || ''; }
function renderLinks(container, rows, code, country, limit) {
  const fragment = document.createDocumentFragment();
  for (const row of rows.slice(0, limit)) {
    const item = node('li', 'muraletta-cities__item');
    const link = node('a', 'muraletta-cities__link');
    link.href = cityUrl(code, row[0]);
    link.append(node('span', 'muraletta-cities__name', row[1]));
    const area = region(row, country);
    if (area) link.append(node('span', 'muraletta-cities__region', area));
    item.append(link); fragment.append(item);
  }
  container.replaceChildren(fragment);
}
function translateUi(container) {
  container.querySelectorAll('[data-city-text]').forEach(element => { element.textContent = t(element.dataset.cityText); });
  container.querySelectorAll('[data-city-search]').forEach(input => {
    input.placeholder = t('search'); input.setAttribute('aria-label', t('search'));
  });
}
async function initDirectory(container) {
  const query = new URL(location.href).searchParams;
  const search = container.querySelector('[data-city-search]');
  const status = container.querySelector('[data-city-status]');
  const list = container.querySelector('[data-city-list]');
  const more = container.querySelector('[data-city-more]');
  const retry = container.querySelector('[data-city-retry]');
  let rows = [], limit = 60, generation = 0, country, code;
  search.value = query.get('q')?.slice(0, 120) || '';
  function show(done = true) {
    const filtered = search.value.trim() ? rows.filter(row => matchesCity(row, search.value)) : rows;
    renderLinks(list, filtered, code, country, limit);
    status.textContent = !done ? t('loading') : filtered.length ? t('count', { count: new Intl.NumberFormat(document.documentElement.lang).format(filtered.length) }) : t('noResults');
    more.hidden = filtered.length <= limit;
  }
  async function load() {
    const turn = ++generation;
    retry.hidden = true; more.hidden = true;
    status.textContent = t('loading');
    try {
      ({ code, country } = await selectedCountry(query.get('pays')));
      container.querySelector('h1').textContent = `${t('cities')} — ${countryName(code)}`;
      document.title = `${t('cities')} — ${countryName(code)} | MURALETTA`;
      if (!country) { status.textContent = t('unsupported'); return; }
      rows = country.preview || []; show(false);
      const loaded = await countryRows(code, partial => { if (turn === generation) { rows = partial; show(false); } });
      if (turn !== generation) return;
      rows = loaded.sort((a, b) => b[5] - a[5] || a[0] - b[0]);
      show();
    } catch {
      status.textContent = t('error'); retry.hidden = false;
    }
  }
  let timer;
  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      limit = 60; if (country) show();
      const url = new URL(location.href);
      if (search.value.trim()) url.searchParams.set('q', search.value.trim()); else url.searchParams.delete('q');
      history.replaceState(history.state, '', url);
    }, 160);
  });
  container.querySelector('form').addEventListener('submit', event => { event.preventDefault(); limit = 60; if (country) show(); });
  more.addEventListener('click', () => { limit += 60; show(); });
  retry.addEventListener('click', load);
  document.addEventListener('muraletta:country-detected', () => { if (!query.has('pays') && code !== currentCountry()) load(); });
  await load();
}
async function initCity(container) {
  const query = new URL(location.href).searchParams;
  const status = container.querySelector('[data-city-status]');
  const retry = container.querySelector('[data-city-retry]');
  const title = container.querySelector('h1');
  const locationLabel = container.querySelector('[data-city-place]');
  const back = container.querySelector('[data-city-back]');
  async function load() {
    retry.hidden = true; status.textContent = t('loading');
    try {
      const { code, country } = await selectedCountry(query.get('pays'));
      back.href = directoryUrl(code);
      const id = Number(query.get('id'));
      if (!country || !Number.isSafeInteger(id) || id <= 0) { status.textContent = t('missing'); return; }
      const part = country.chunks.find(item => item.minId <= id && item.maxId >= id);
      const row = part && (await chunk(part)).find(item => item[0] === id);
      if (!row) { status.textContent = t('missing'); return; }
      title.textContent = row[1];
      locationLabel.textContent = [region(row, country), countryName(code)].filter(Boolean).join(' · ');
      status.textContent = t('empty');
      container.dataset.cityId = String(id);
      container.dataset.cityCountry = code;
      document.title = `${row[1]} — ${countryName(code)} | MURALETTA`;
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', document.title);
      document.querySelector('meta[name="description"]')?.setAttribute('content', `${row[1]} — ${countryName(code)}. ${t('empty')}`);
    } catch { status.textContent = t('error'); retry.hidden = false; }
  }
  retry.addEventListener('click', load);
  await load();
}
function initHero(form, index, header = false) {
  const input = form.querySelector('input[name="q"]');
  if (!input || form.dataset.cityReady) return;
  form.dataset.cityReady = 'true';
  const panel = node('div', 'muraletta-city-suggestions'); panel.hidden = true;
  const list = node('ul', 'muraletta-cities__list'); list.id = `muraletta-city-suggestions-${index}`;
  const status = node('p', 'muraletta-city-suggestions__status'); status.setAttribute('aria-live', 'polite');
  const all = node('a', 'muraletta-city-suggestions__all', t('all'));
  const products = node('a', 'muraletta-city-suggestions__all', t('products'));
  panel.append(status, list, all, products);
  if (header) form.querySelector('.predictive-search-form__header')?.after(panel); else form.append(panel);
  if (!header) { input.setAttribute('aria-controls', list.id); input.setAttribute('aria-expanded', 'false'); }
  input.setAttribute('autocomplete', 'off');
  let turn = 0, timer;
  const close = () => { panel.hidden = true; if (!header) input.setAttribute('aria-expanded', 'false'); };
  async function show() {
    const version = ++turn, query = input.value.trim().slice(0, 120);
    if (!query) { close(); return; }
    const code = currentCountry();
    all.href = directoryUrl(code, query);
    const productUrl = new URL(`${root}search`, location.origin); productUrl.searchParams.set('q', query); productUrl.searchParams.set('type', 'product');
    products.href = productUrl;
    panel.hidden = false; if (!header) input.setAttribute('aria-expanded', 'true');
    status.textContent = t('loading');
    try {
      const data = await manifest(), country = data.countries[code];
      if (!country) { status.textContent = t('unsupported'); list.replaceChildren(); return; }
      const update = (rows, done) => {
        if (version !== turn) return;
        const matches = rows.filter(row => matchesCity(row, query));
        matches.sort((a, b) => Number(normalizeCity(b[1]) === normalizeCity(query)) - Number(normalizeCity(a[1]) === normalizeCity(query)) || b[5] - a[5]);
        renderLinks(list, matches, code, country, 8);
        status.textContent = done ? `${t('cities')} — ${countryName(code)}` : t('loading');
        if (done && !matches.length) status.textContent = t('noResults');
      };
      update(country.preview || [], false);
      update(await countryRows(code, rows => update(rows, false)), true);
    } catch { if (version === turn) status.textContent = t('error'); }
  }
  input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(show, 180); });
  input.addEventListener('focus', () => { if (input.value.trim()) show(); });
  if (!header) form.addEventListener('submit', event => { if (input.value.trim()) { event.preventDefault(); location.assign(directoryUrl(currentCountry(), input.value.trim())); } });
  form.addEventListener('keydown', event => { if (event.key === 'Escape') { close(); if (!header) { event.preventDefault(); input.focus(); } } });
  form.addEventListener('focusout', () => setTimeout(() => { if (!form.contains(document.activeElement)) close(); }, 0));
  document.addEventListener('click', event => { if (!form.contains(event.target)) close(); });
  document.addEventListener('muraletta:country-detected', () => { if (!panel.hidden) show(); });
}
function init() {
  if (!config) return;
  document.querySelectorAll('[data-city-directory-link]').forEach(link => { link.textContent = t('all'); link.href = directoryUrl(); });
  document.querySelectorAll('[data-city-page]').forEach(container => {
    translateUi(container);
    if (container.dataset.cityPage === 'directory') initDirectory(container); else initCity(container);
  });
  document.querySelectorAll('.muraletta-hero__search').forEach((form, index) => initHero(form, index));
  document.querySelectorAll('.predictive-search-form').forEach((form, index) => initHero(form, `header-${index}`, true));
  const addMenuLinks = () => document.querySelectorAll('[data-local-country-menu]').forEach(menu => {
    const mobile = !!menu.closest('header-drawer');
    const list = menu.querySelector(mobile ? '.menu-drawer__menu--childlist' : '.mega-menu__list');
    if (!list || list.querySelector('[data-city-directory-menu]')) return;
    const item = node('li', mobile ? 'menu-drawer__list-item' : 'mega-menu__column mega-menu__column--span-1');
    item.dataset.cityDirectoryMenu = 'true';
    const link = node('a', mobile ? 'menu-drawer__menu-item menu-drawer__menu-item--child' : 'mega-menu__link', t('all'));
    link.href = directoryUrl(); item.append(link); list.append(item);
  });
  addMenuLinks();
  document.addEventListener('muraletta:country-detected', addMenuLinks);
  const header = document.getElementById('header-group');
  if (header) new MutationObserver(addMenuLinks).observe(header, { subtree: true, childList: true });
}
init();
